// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { brokerageApi } from '@shared/data-access/api-base/citec-brokerage-api';
import {
  type DeleteAdvisoryProposalRequestBody,
  type UpdateAdvisoryProposalRequestBody,
  type AggregatorClientProfileResponseBody,
  type CitecUserClientProfileResponseBody,
  type UpdateCitecUserClientProfileRequestBody,
  type CreateAdvisoryProposalRequestBody,
  type AdvisoryProposalContractResponseBody,
  type UpdateAdvisoryContractRequestBody,
  type UpdateAdvisoryContractResponseBody,
} from '@shared/data-access/interfaces/api/brokerage/client-types';
import { type AggregatorProposalParams } from '@shared/data-access/interfaces/api/brokerage/proposals-types';

/**
 * Advisory API - Citec Brokerage Integration.
 *
 * This API layer provides access to the **Citec Brokerage REST API** for managing
 * advisory proposals.
 *
 * @remarks
 * Use this API to:
 *
 * - Create advisory proposals for clients.
 * - Post notifications.
 *
 * Backed by RTK Query, this module abstracts RESTful operations into React hooks
 * for seamless integration with front-end applications.
 * @see {@link https://citecsolutions.com/apis} for full API reference.
 */
export const advisoryApi = brokerageApi.injectEndpoints({
  endpoints: build => ({
    /**
     * Post Notification
     *
     * This endpoint posts a notification.
     *
     * @remarks
     * This endpoint is used to post a notification with the specified type.
     * @example
     *
     * ```tsx
     * const [
     *   postNotification, // This is the mutation trigger
     *   { isLoading: isPosting }, // This is the destructured mutation result
     * ] = advisoryApi.useNotificationMutation();
     *
     * postNotification({ type: 'advisory_proposal', client_id: 123 });
     * ```
     *
     * @param body - An object containing the notification type and client ID.
     * @returns Void response upon successful creation.
     */
    notification: build.mutation<
      void,
      AggregatorProposalParams & { type: 'advisory_proposal' }
    >({
      /**
       * Builds the query URL and method for the post notification endpoint.
       *
       * @param body - An object containing the notification type and client ID.
       * @returns An object containing the URL and method for the query.
       */
      query: body => ({
        url: '/v2/notification/',
        method: 'POST',
        body,
      }),
      extraOptions: {
        useTrackPromise: true,
      },
    }),

    /**
     * Fetch a client profile by client ID.
     *
     * @remarks
     * This endpoint retrieves a client profile by the specified client ID.
     * @example
     *
     * ```tsx
     * const { data } = clientApi.useGetClientProfileQuery({
     *   client_id: '123',
     * });
     * ```
     *
     * @param body - An object containing the `client_id` to filter the profile.
     * @returns A {@link AggregatorClientProfileResponseBody} containing the client
     *   profile information.
     */
    getClientProfile: build.query<
      AggregatorClientProfileResponseBody,
      AggregatorProposalParams
    >({
      /**
       * Builds the query URL and method for the `getClientProfile` endpoint.
       *
       * @param params - An object containing the `client_id` to filter the profile.
       * @returns An object containing the `url` and `method` for the query.
       */
      query: ({ client_id }) => ({
        url: `/v2/advisory/clients/${client_id}/profile/`,
        method: 'GET',
      }),

      /**
       * Provides tags for the `getClientProfile` endpoint.
       *
       * @param result - The result of the query.
       * @param error - An error object if the query fails.
       * @param params - An object containing the `client_id` to filter the profile.
       * @returns An array of tags for the endpoint.
       */
      providesTags: [{ type: 'Client', id: 'Profile' }],
    }),

    /**
     * Get Current Citec User Client Profile
     *
     * Retrieves the profile for the current client context without requiring a
     * specific `client_id`.
     *
     * @example
     *
     * ```tsx
     * const { data, isLoading } =
     *   advisoryApi.useGetCitecUserClientProfileQuery();
     * ```
     *
     * @returns The current client profile payload.
     */
    getCitecUserClientProfile: build.query<
      CitecUserClientProfileResponseBody,
      void
    >({
      query: () => ({
        url: '/v2/advisory/clients/profile/',
        method: 'GET',
      }),
      extraOptions: {
        useTrackPromise: true,
      },
      providesTags: [{ type: 'Client', id: 'Profile' }],
    }),

    /**
     * Update Current Citec User Client Profile
     *
     * Updates the current client's profile data. Only accepts editable fields
     * (e.g., name fields, address, etc.).
     *
     * @example
     *
     * ```tsx
     * const [updateProfile, { isLoading }] =
     *   advisoryApi.useUpdateCitecUserClientProfileMutation();
     *
     * updateProfile({ first_name: 'John', first_surname: 'Doe' });
     * ```
     */
    updateCitecUserClientProfile: build.mutation<
      void,
      UpdateCitecUserClientProfileRequestBody
    >({
      query: body => ({
        url: '/v2/advisory/clients/profile/',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'Client', id: 'Profile' }],
    }),

    /**
     * Create Advisory Proposal
     *
     * This endpoint creates an advisory proposal for a client.
     *
     * @remarks
     * This endpoint is used to create an advisory proposal with the specified
     * client ID, institutions IDs, advisory type, and commission managed assets.
     * @example
     *
     * ```tsx
     * const [
     *   createAdvisoryProposal, // This is the mutation trigger
     *   { isLoading: isCreating }, // This is the destructured mutation result
     * ] = advisoryApi.useCreateAdvisoryProposalMutation();
     *
     * createAdvisoryProposal({
     *   client_id: 123,
     *   institutions_ids: ['institution1', 'institution2'],
     *   advisory_type: 'type1',
     *   commission_managed_assets: 100,
     * });
     * ```
     *
     * @param body - An object containing the client ID, institutions IDs, advisory
     *   type, and commission managed assets.
     * @returns Void response upon successful creation.
     */
    createAdvisoryProposal: build.mutation<void, CreateAdvisoryProposalRequestBody>(
      {
        /**
         * Builds the query URL and method for the create advisory proposal1
         * endpoint.
         *
         * @param body - An object containing the client ID, institutions IDs,
         *   advisory type, and commission managed assets.
         * @returns An object containing the URL and method for the query.
         */
        query: ({ id: client_id, ...body }) => ({
          url: '/v2/advisory/proposals/',
          method: 'POST',
          body: {
            client_id,
            ...body,
          },
        }),
        invalidatesTags: (_, error) =>
          error
            ? []
            : [
                { type: 'Client', id: 'Profile' },
                { type: 'Aggregator', id: 'List' },
                { type: 'Client', id: 'List' },
              ],
      },
    ),

    updateAdvisoryProposal: build.mutation<void, UpdateAdvisoryProposalRequestBody>(
      {
        query: ({ proposal_id, ...body }) => ({
          url: `/v2/advisory/proposals/${proposal_id}/`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (_, error) =>
          error
            ? []
            : [
                {
                  type: 'Client',
                  id: 'Profile',
                },
              ],
      },
    ),

    deleteAdvisoryProposal: build.mutation<void, DeleteAdvisoryProposalRequestBody>(
      {
        query: ({ proposal_id }) => ({
          url: `/v2/advisory/proposals/${proposal_id}/`,
          method: 'DELETE',
        }),
        invalidatesTags: (_, error) =>
          error
            ? []
            : [
                {
                  type: 'Client',
                  id: 'Profile',
                },
              ],
      },
    ),

    /**
     * Check Retail Access
     *
     * Verifies retail access eligibility for a client email.
     *
     * @remarks
     * Checks if a client with the specified email has permission for retail access
     * features. Primarily used for access control and feature gating.
     * @example
     *
     * ```tsx
     * const { data: accessInfo, error: accessError } =
     *   advisoryApi.useCheckRetailAccessQuery({
     *     client_email: 'john@doe.com',
     *   });
     * ```
     *
     * @param client_email - Client email to verify access
     * @returns Access verification status (void indicates default success)
     */
    checkRetailAccess: build.query<void, { client_email: string }>({
      query: ({ client_email }) => ({
        url: '/v2/advisory/clients/access/',
        method: 'GET',
        params: { client_email },
      }),
      providesTags: [{ type: 'Client', id: 'List' }],
    }),

    /**
     * Get Advisory Proposals
     *
     * Retrieves all advisory proposals.
     *
     * @remarks
     * Fetches the complete list of advisory proposals. Automatically caches results
     * and provides cache invalidation through tags.
     * @example
     *
     * ```tsx
     * const {
     *   data: proposals,
     *   isLoading: isLoadingProposals,
     *   refetch,
     * } = advisoryApi.useGetAdvisoryProposalsQuery();
     * ```
     *
     * @returns Array of advisory proposal objects
     */
    getAdvisoryProposals: build.query<AdvisoryProposalContractResponseBody[], void>(
      {
        query: () => ({
          url: '/v2/advisory/proposals/',
          method: 'GET',
        }),
        providesTags: [{ type: 'Proposals', id: 'List' }],
      },
    ),

    updateAdvisoryContract: build.mutation<
      UpdateAdvisoryContractResponseBody,
      UpdateAdvisoryContractRequestBody
    >({
      query: body => ({
        url: `/v2/advisory/proposals/contracts/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error, { calculate_test_results }) =>
        error || !calculate_test_results
          ? []
          : [
              {
                type: 'Proposals',
                id: 'List',
              },
            ],
    }),

    signAdvisoryContract: build.mutation<void, { proposal_id: number }>({
      query: ({ proposal_id }) => ({
        url: `/v2/advisory/proposals/${proposal_id}/sign/`,
        method: 'POST',
      }),
      async onQueryStarted({ proposal_id }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          advisoryApi.util.updateQueryData(
            'getAdvisoryProposals',
            undefined,
            drafts => {
              const proposal = drafts?.find(item => item.id === proposal_id);

              if (proposal?.contract) {
                proposal.contract.status = 'sent';
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_, error) =>
        error
          ? []
          : [
              {
                type: 'Proposals',
                id: 'List',
              },
            ],
    }),
  }),
});
